// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/services/ServiceDialogsHost.tsx
// 🔧 PURPOSE: I servizi della Home, in un posto solo: consegna, auto,
//             escursioni (+ il planner dell'escursione su misura),
//             massaggi, bici, emergenze, rifiuti, gioco.
//             Qualunque pezzo della Home li apre con
//             useServiceDialogs().openService('rentBike' ...) senza sapere
//             come sono fatti. Prima erano finestre sopra la Home; ora ognuno
//             è una pagina a tutto schermo col suo indirizzo
//             (lib/servicePages, src/pages/services): openService ci porta
//             lì, e il tasto indietro torna alla Home.
//
//             Qui vivono anche i vecchi link con l'ancora, che messaggi e
//             notifiche già inviati possono ancora contenere:
//             #wine / #clean / #breakfast / #extras portano agli Extra,
//             #bike e #recycle aprono la loro pagina (la Home resta sotto,
//             senza l'ancora: indietro si torna lì).
// ============================================

import React, { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { servicePath, useOpenServicePage, type OpenServiceOptions, type ServiceKind } from '../../../lib/servicePages';

export type { OpenServiceOptions, ServiceKind } from '../../../lib/servicePages';

export interface ServiceDialogsApi {
  /**
   * Apre la pagina del servizio (una voce nuova: indietro si torna qui, o a
   * `options.backTo`, es. la Home col foglio aperto). Finché la pagina non è
   * arrivata, un altro tocco non fa niente.
   */
  openService: (kind: ServiceKind, options?: OpenServiceOptions) => void;
}

const ServiceDialogsContext = createContext<ServiceDialogsApi | null>(null);

/**
 * Fuori dall'host (non dovrebbe capitare: sta nella Home) la pagina si apre
 * lo stesso, caricando l'indirizzo; in console resta l'avviso.
 */
const NO_HOST: ServiceDialogsApi = {
  openService: (kind) => {
    console.warn(`ServiceDialogsHost assente: "${kind}" si apre ricaricando la pagina`);
    window.location.assign(servicePath(kind));
  },
};

export const useServiceDialogs = (): ServiceDialogsApi => useContext(ServiceDialogsContext) || NO_HOST;

/** I vecchi link con l'ancora che aprono la pagina di un servizio. */
const HASH_SERVICES: Record<string, ServiceKind> = {
  bike: 'rentBike',
  recycle: 'recycle',
};

const ServiceDialogsHost: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const openService = useOpenServicePage();

  // I vecchi link con l'ancora (#bike, #recycle, #wine...).
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (!hash) return undefined;
    // Un attimo dopo, come prima: la Home è montata.
    const timer = setTimeout(() => {
      switch (hash) {
        // I vecchi link #wine / #clean / #breakfast aprivano modali che
        // mandavano un messaggio in chat con prezzi scritti nel codice.
        // Adesso portano dove si compra davvero.
        case 'wine':
        case 'clean':
        case 'breakfast':
        case 'extras':
          navigate('/extras');
          return;
        default:
          break;
      }
      // L'ancora si toglie (sostituendo la voce) e, se è di un servizio, la
      // sua pagina si apre sopra: indietro si torna alla Home pulita.
      navigate(location.pathname, { replace: true });
      const kind = HASH_SERVICES[hash];
      if (kind) navigate(servicePath(kind));
    }, 100);
    return () => clearTimeout(timer);
  }, [location.hash, navigate, location.pathname]);

  const api = useMemo<ServiceDialogsApi>(() => ({ openService }), [openService]);

  return <ServiceDialogsContext.Provider value={api}>{children}</ServiceDialogsContext.Provider>;
};

export default ServiceDialogsHost;
